import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiService } from "../../services/apiService";
import { formatStandardAddress, formatInputAddress } from "../../utils/helpers";
import { canMutateTenantData } from "../../config/constants";
import Toast from "../ui/Toast";
import YmdDateInput from "../ui/YmdDateInput";

import CustomSelect from "../ui/CustomSelect";
function parseLocalDate(value) {
  if (!value) return null;
  if (typeof value === "string") {
    return new Date(value.includes("T") ? value : value + "T00:00:00");
  }
  return new Date(value);
}

function ymdSnapshotField(v) {
  if (v == null || v === "") return "";
  if (typeof v === "string") return v.split("T")[0].split(" ")[0];
  try {
    // const d = new Date(v);
    const d = parseLocalDate(v);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
}

function normalizeSnapshotContacts(siteContacts) {
  const empty = { name: "", title: "", email: "", phone: "" };
  const out = [{ ...empty }, { ...empty }, { ...empty }];
  let arr = siteContacts;
  if (typeof arr === "string") {
    try {
      arr = JSON.parse(arr);
    } catch {
      arr = [];
    }
  }
  if (!Array.isArray(arr)) return out;
  arr.slice(0, 3).forEach((c, i) => {
    if (!c || typeof c !== "object") return;
    out[i] = {
      name: c.name != null ? String(c.name) : "",
      title: c.title != null ? String(c.title) : "",
      email: c.email != null ? String(c.email) : "",
      phone: c.phone != null ? String(c.phone) : "",
    };
  });
  return out;
}

/**
 * Derive site form state from inventory snapshot rows (same address group).
 */
function buildSnapshotSiteState(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      noSite: true,
      site: null,
      gmFallbackName: null,
      form: {
        region: "",
        site_name: "",
        site_classification: "",
        general_manager_id: "",
        sd_wan_appliance_name: "",
        site_status: "Active",
        closed_date: "",
        scheduled_close_date: "",
        contacts: normalizeSnapshotContacts(null),
      },
    };
  }

  const scoreRow = (r) => {
    if (!r) return -1;
    let s = 0;
    if (r.site_region) s += 2;
    if (r.site_name) s += 1;
    if (r.site_classification) s += 2;
    if (r.site_status) s += 1;
    if (r.general_manager_id != null && r.general_manager_id !== "") s += 2;
    if (r.general_manager_name) s += 1;
    if (r.sd_wan_appliance_name) s += 1;
    if (Array.isArray(r.site_contacts) && r.site_contacts.length) s += 2;
    return s;
  };

  const row = items.reduce(
    (best, r) => (scoreRow(r) > scoreRow(best) ? r : best),
    items[0],
  );

  const contacts = normalizeSnapshotContacts(row.site_contacts);
  const hasData = !!(
    row.site_region ||
    (row.site_name && String(row.site_name).trim()) ||
    row.site_classification ||
    (row.general_manager_id != null && row.general_manager_id !== "") ||
    (row.general_manager_name && String(row.general_manager_name).trim()) ||
    (row.sd_wan_appliance_name && String(row.sd_wan_appliance_name).trim()) ||
    row.site_status ||
    contacts.some((c) => c.name || c.title || c.email || c.phone)
  );

  const statusRaw = (row.site_status || "").toString().trim();
  const siteStatus = statusRaw.toLowerCase() === "closed" ? "Closed" : "Active";

  return {
    noSite: !hasData,
    site:
      row.site_id != null && row.site_id !== ""
        ? { id: row.site_id }
        : { id: null },
    gmFallbackName: row.general_manager_name
      ? String(row.general_manager_name).trim()
      : null,
    form: {
      region: row.site_region || "",
      site_name: row.site_name || "",
      site_classification: row.site_classification || "",
      general_manager_id:
        row.general_manager_id != null && row.general_manager_id !== ""
          ? String(row.general_manager_id)
          : "",
      sd_wan_appliance_name: row.sd_wan_appliance_name || "",
      site_status: siteStatus,
      closed_date: ymdSnapshotField(row.site_closed_date || row.closed_date),
      scheduled_close_date: ymdSnapshotField(
        row.site_scheduled_close_date || row.scheduled_close_date,
      ),
      contacts,
    },
  };
}

const SiteDetailsModal = ({
  open,
  onClose,
  addressKey,
  /** When an array, site fields are read-only from these snapshot inventory rows (no API load). */
  snapshotGroupItems,
  tenant,
  tenantId,
  userRole,
  onSaved,
  allowCreate = true,
}) => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const minDate =
    `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [noSite, setNoSite] = useState(false);
  const [toast, setToast] = useState({ message: null, type: "success" });
  const [site, setSite] = useState(null);
  const [siteAddresses, setSiteAddresses] = useState(null);
  const [regions, setRegions] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [generalManagers, setGeneralManagers] = useState([]);
  const [newGMName, setNewGMName] = useState("");
  const [addingGM, setAddingGM] = useState(false);
  const [snapshotGmFallbackName, setSnapshotGmFallbackName] = useState(null);
  const [form, setForm] = useState({
    region: "",
    site_name: "",
    site_classification: "",
    general_manager_id: "",
    sd_wan_appliance_name: "",
    site_status: "Active",
    closed_date: "",
    scheduled_close_date: "",
    contacts: [
      { name: "", title: "", email: "", phone: "" },
      { name: "", title: "", email: "", phone: "" },
      { name: "", title: "", email: "", phone: "" },
    ],
  });

  const effectiveTenantId = tenantId || tenant?.id || tenant?.tenant_id || tenant?.tenantId;
  const fromSnapshot = Array.isArray(snapshotGroupItems);
  const readOnly = !allowCreate;
  const canEdit =
    !readOnly && canMutateTenantData(userRole) && !fromSnapshot;
  const canSetClosed = userRole === "Admin" || userRole === "Client Admin";
  const siteStatusOptions = useMemo(() => {
    const base = canSetClosed ? ["Active", "Closed"] : ["Active"];
    // Viewers cannot set Closed, but must see the real status when a site is already closed.
    if (!form.site_status) return base;
    if (base.includes(form.site_status)) return base;
    return [...base, form.site_status];
  }, [canSetClosed, form.site_status]);

  const regionOptions = useMemo(() => {
    const base = regions || [];
    if (!fromSnapshot || !form.region) return base;
    if (base.includes(form.region)) return base;
    return [...base, form.region];
  }, [regions, form.region, fromSnapshot]);

  const classificationOptions = useMemo(() => {
    const base = classifications || [];
    if (!form.site_classification) return base;
    if (base.includes(form.site_classification)) return base;
    return [...base, form.site_classification];
  }, [classifications, form.site_classification]);

  const gmOptions = useMemo(() => {
    const base = generalManagers || [];
    if (!fromSnapshot || !form.general_manager_id) return base;
    const idStr = String(form.general_manager_id);
    if (base.some((g) => String(g.id) === idStr)) return base;
    const name = snapshotGmFallbackName || `ID ${idStr}`;
    return [...base, { id: form.general_manager_id, name }];
  }, [
    generalManagers,
    form.general_manager_id,
    fromSnapshot,
    snapshotGmFallbackName,
  ]);

  const loadSite = useCallback(async () => {
    if (!open || !addressKey) return;

    const isSnap = Array.isArray(snapshotGroupItems);
    if (isSnap) {
      setLoading(true);
      setError(null);
      setNoSite(false);
      setSiteAddresses(null);
      setSnapshotGmFallbackName(null);
      const built = buildSnapshotSiteState(snapshotGroupItems);
      setNoSite(built.noSite);
      setSite(built.site);
      setForm(built.form);
      setSnapshotGmFallbackName(built.gmFallbackName || null);
      setLoading(false);
      return;
    }

    if (!effectiveTenantId) return;

    setSnapshotGmFallbackName(null);
    setLoading(true);
    setError(null);
    setNoSite(false);
    setSiteAddresses(null);
    try {
      const res = await apiService.getSiteByAddressKey(
        effectiveTenantId,
        {
          std_street_address: addressKey.std_street_address,
          std_city: addressKey.std_city,
          std_state: addressKey.std_state,
          std_zip: addressKey.std_zip,
          std_country: addressKey.std_country,
        },
        { create: false }
      );
      if (res.success && res.data) {
        setSite(res.data);
        let initialRegion = res.data.region || "";
        if (!initialRegion && addressKey.std_zip) {
          const r = await apiService.getRegionByZip(addressKey.std_zip);
          if (r.success && r.data) initialRegion = r.data;
        }
        setForm({
          region: initialRegion,
          site_name: res.data.site_name || "",
          site_classification: res.data.site_classification || "",
          general_manager_id: res.data.general_manager_id != null ? String(res.data.general_manager_id) : "",
          sd_wan_appliance_name: res.data.sd_wan_appliance_name || "",
          site_status: res.data.site_status || "Active",
          // closed_date: res.data.closed_date || "",
          // scheduled_close_date: res.data.scheduled_close_date || "",
          closed_date: ymdSnapshotField(res.data.closed_date),
scheduled_close_date: ymdSnapshotField(res.data.scheduled_close_date),
          contacts: [
            res.data.contacts?.[0] ? { name: res.data.contacts[0].name || "", title: res.data.contacts[0].title || "", email: res.data.contacts[0].email || "", phone: res.data.contacts[0].phone || "" } : { name: "", title: "", email: "", phone: "" },
            res.data.contacts?.[1] ? { name: res.data.contacts[1].name || "", title: res.data.contacts[1].title || "", email: res.data.contacts[1].email || "", phone: res.data.contacts[1].phone || "" } : { name: "", title: "", email: "", phone: "" },
            res.data.contacts?.[2] ? { name: res.data.contacts[2].name || "", title: res.data.contacts[2].title || "", email: res.data.contacts[2].email || "", phone: res.data.contacts[2].phone || "" } : { name: "", title: "", email: "", phone: "" },
          ],
        });
        if (res.data.id) {
          const addrRes = await apiService.getSiteAddresses(res.data.id, effectiveTenantId);
          if (addrRes.success && addrRes.data) setSiteAddresses(addrRes.data);
        }
      } else {
        setError(res.message || "Failed to load site details");
      }
    } catch (err) {
      if (err.status === 404 || err.details?.error === "Site not found") {
        setNoSite(true);
        setSite(null);
        setError(null);
        if (allowCreate) {
          let initialRegion = "";
          if (addressKey.std_zip) {
            try {
              const r = await apiService.getRegionByZip(addressKey.std_zip);
              if (r.success && r.data) initialRegion = r.data;
            } catch (_) {}
          }
          setForm({
            region: initialRegion,
            site_name: "",
            site_classification: "",
            general_manager_id: "",
            sd_wan_appliance_name: "",
            site_status: "Active",
            closed_date: "",
            scheduled_close_date: "",
            contacts: [
              { name: "", title: "", email: "", phone: "" },
              { name: "", title: "", email: "", phone: "" },
              { name: "", title: "", email: "", phone: "" },
            ],
          });
        }
      } else {
        setError(err.message || "Failed to load site details");
      }
    } finally {
      setLoading(false);
    }
  }, [open, effectiveTenantId, addressKey, allowCreate, snapshotGroupItems]);

  useEffect(() => {
    loadSite();
  }, [loadSite]);

  // Lock body scroll when modal is open to avoid double scrollbars
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    // Reserve space for scrollbar to prevent layout shift (optional, helps if page had scrollbar)
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const fetchOptions = async () => {
      try {
        const [regRes, classRes, gmRes] = await Promise.all([
          apiService.getRegions(),
          apiService.getSiteClassifications(effectiveTenantId),
          apiService.getGeneralManagers(effectiveTenantId),
        ]);
        if (regRes.success && regRes.data) setRegions(regRes.data);
        if (classRes.success && classRes.data) setClassifications(classRes.data);
        if (gmRes.success && gmRes.data) setGeneralManagers(gmRes.data);
      } catch (e) {
        console.warn("Options fetch failed:", e);
      }
    };
    fetchOptions();
  }, [open, effectiveTenantId, addressKey]);

  const handleChange = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "site_status") {
        if (value === "Closed") {
          // next.closed_date = new Date().toISOString().slice(0, 10);
          next.closed_date = new Date().toISOString().split("T")[0];
          next.scheduled_close_date = "";
        } else if (value === "Active") {
          next.closed_date = "";
        }
      }
      return next;
    });
    setError(null);
  };

  const handleContactChange = (index, field, value) => {
    setForm((prev) => {
      const next = [...prev.contacts];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, contacts: next };
    });
  };

  const handleAddGeneralManager = async () => {
    const name = (newGMName || "").trim();
    if (!name || !effectiveTenantId || !canEdit) return;
    setAddingGM(true);
    setError(null);
    try {
      const res = await apiService.createGeneralManager(effectiveTenantId, name);
      if (res.success && res.data) {
        setGeneralManagers((prev) => [...prev, res.data].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
        setForm((prev) => ({ ...prev, general_manager_id: String(res.data.id) }));
        setNewGMName("");
      } else setError(res.message || "Failed to add General Manager");
    } catch (err) {
      setError(err.message || "Failed to add General Manager");
    } finally {
      setAddingGM(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!effectiveTenantId || !canEdit) return;
    const isCreate = !site?.id && allowCreate && addressKey;
    if (!site?.id && !isCreate) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        tenant_id: effectiveTenantId,
        version: site?.version,
        region: form.region || null,
        site_name: form.site_name || null,
        site_classification: form.site_classification || null,
        general_manager_id: form.general_manager_id === "" ? null : form.general_manager_id,
        sd_wan_appliance_name: form.sd_wan_appliance_name || null,
        site_status: form.site_status || "Active",
        scheduled_close_date: form.scheduled_close_date || null,
        contacts: form.contacts.map((c) => ({
          name: c.name || null,
          title: c.title || null,
          email: c.email || null,
          phone: c.phone || null,
        })),
      };
      let res;
      if (isCreate) {
        res = await apiService.createSite({
          ...payload,
          std_street_address: addressKey.std_street_address,
          std_city: addressKey.std_city,
          std_state: addressKey.std_state,
          std_zip: addressKey.std_zip,
          std_country: addressKey.std_country,
        });
      } else {
        res = await apiService.updateSite(site.id, payload);
      }
      if (res.success) {
        setToast({
          message: isCreate
            ? "Site created successfully"
            : "Site details saved successfully",
          type: "success",
        });
        setSite(res.data);
        setNoSite(false);
        onSaved && onSaved(res.data);
        setTimeout(() => handleClose(), 800);
      } else {
        setError(res.message || "Failed to save");
      }
    } catch (err) {
      console.error("Save site error:", err);
    
      if (err.status === 409) {
        setToast({
          type: "warning",
          title: "Changes Not Saved",
          message:
            "This site was modified by another user while you were editing it.\nPlease refresh the page to load the latest changes and try again.",
        });
      } else {
        setError(err.message || "Failed to save site details");
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const displayAddress = formatStandardAddress({
    std_street_address: addressKey?.std_street_address,
    std_city: addressKey?.std_city,
    std_state: addressKey?.std_state,
    std_zip: addressKey?.std_zip,
    std_country: addressKey?.std_country,
  });

  const handleClose = () => {
    setToast({
      title: "",
      message: null,
      type: "success",
    });
  
    setError(null);
  
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-5 bg-[#1c1c1a]/25 backdrop-blur-[4px] overflow-hidden"
      style={{ WebkitBackdropFilter: "blur(4px)" }}
      role="presentation"
      onClick={handleClose}
    >
      <div
        className="relative z-[1] flex max-h-[92vh] w-full max-w-[920px] flex-col overflow-hidden rounded-[12px] bg-white shadow-[0_24px_80px_rgba(28,28,26,0.28)]"
        role="dialog"
        aria-labelledby="site-details-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-[var(--line,#e4e2dc)] px-6 py-4 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#a51c1c]">
              Site details
            </p>
            <h2
              id="site-details-title"
              className="mt-1 truncate text-[22px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]"
            >
              {displayAddress || "Site"}
            </h2>
            {fromSnapshot ? (
              <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)]">
                Historical snapshot (read-only)
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="shrink-0 rounded-md p-1 text-[26px] leading-none text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)] hover:text-[var(--ink,#1c1c1a)]"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
          <Toast title={toast.title} message={toast.message} type={toast.type} 
          onClose={() =>
            setToast({
              title: "",
              message: null,
              type: "success",
            })}
           />
          {error && (
            <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm" role="alert">
              {error}
            </div>
          )}
          {loading ? (
            <div className="py-8 text-center text-gray-500">Loading site details...</div>
          ) : noSite && !allowCreate ? (
            <div className="py-6">
              <p className="text-sm text-gray-600 mb-2" title={displayAddress}>
                {displayAddress}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {fromSnapshot ? (
                  "No site details were captured on this snapshot for this address."
                ) : (
                  <>
                    No site details configured for this address. Add site
                    details from <strong>Master Address</strong> to see them
                    here.
                  </>
                )}
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 rounded text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Standardized address</p>
                <p className="text-sm text-gray-900 font-medium" title={displayAddress}>
                  {displayAddress}
                </p>
              </div>
              {/* {siteAddresses && (
                <div className="mb-4 p-3 rounded border border-gray-200 bg-gray-50/50">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Raw addresses under this site ({siteAddresses.raw_address_count})
                  </p>
                  {siteAddresses.raw_address_count === 0 ? (
                    <p className="text-sm text-gray-500">No address records.</p>
                  ) : (
                    <ul className="space-y-2 text-sm text-gray-700">
                      {siteAddresses.raw_addresses.map((raw, idx) => (
                        <li key={raw.address_id || idx} className="flex items-start gap-2">
                          <span className="text-gray-400 font-mono text-xs flex-shrink-0">{idx + 1}.</span>
                          <span title={formatInputAddress(raw)}>{formatInputAddress(raw)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )} */}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
                  <input
                    type="text"
                    value={form.site_name}
                    onChange={(e) => handleChange("site_name", e.target.value)}
                    disabled={!canEdit}
                    placeholder="Enter site name"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                    <CustomSelect
                    value={form.region}
                    onChange={(e) => handleChange("region", e.target.value)}
                    disabled={!canEdit}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                  >
                    <option value="">— Select —</option>
                    {regionOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </CustomSelect>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Classification</label>
                  <CustomSelect
                    value={form.site_classification}
                    onChange={(e) => handleChange("site_classification", e.target.value)}
                    disabled={!canEdit}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                  >
                    <option value="">— Select —</option>
                    {classificationOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </CustomSelect>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">General Manager</label>
                  <div className="flex gap-2 flex-wrap items-end">
                    <CustomSelect
                      value={form.general_manager_id}
                      onChange={(e) => handleChange("general_manager_id", e.target.value)}
                      disabled={!canEdit}
                      className="flex-1 min-w-0 border border-gray-300 rounded px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                    >
                      <option value="">— None —</option>
                      {gmOptions.map((gm) => (
                        <option key={gm.id} value={gm.id}>{gm.name}</option>
                      ))}
                    </CustomSelect>
                    {canEdit && (
                      <div className="flex gap-1 items-center flex-shrink-0">
                        <input
                          type="text"
                          placeholder="New GM name"
                          value={newGMName}
                          onChange={(e) => setNewGMName(e.target.value)}
                          className="w-32 border border-gray-300 rounded px-2 py-1.5 text-sm"
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddGeneralManager())}
                        />
                        <button
                          type="button"
                          onClick={handleAddGeneralManager}
                          disabled={addingGM || !(newGMName || "").trim()}
                          className="px-2 py-1.5 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded disabled:opacity-50"
                        >
                          {addingGM ? "..." : "Add"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SD WAN Appliance Name</label>
                  <input
                    type="text"
                    value={form.sd_wan_appliance_name}
                    onChange={(e) => handleChange("sd_wan_appliance_name", e.target.value)}
                    disabled={!canEdit}
                    placeholder="If applicable"
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm disabled:bg-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site Status</label>
                  <CustomSelect
                    value={form.site_status}
                    onChange={(e) => handleChange("site_status", e.target.value)}
                    disabled={!canEdit}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                  >
                    {siteStatusOptions.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </CustomSelect>
                  {!canSetClosed && (
                    <p className="text-xs text-gray-500 mt-1">Only Admin users can set site to Closed.</p>
                  )}
                </div>

                {form.site_status === "Closed" && form.closed_date && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Closed Date</label>
                    <YmdDateInput
                      value={form.closed_date}
                      disabled
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-gray-100 text-gray-600"
                    />
                    <p className="text-xs text-gray-500 mt-1">Date the site was closed.</p>
                  </div>
                )}

                {form.site_status === "Active" && canSetClosed && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled Close Date</label>
                    <YmdDateInput
                      value={form.scheduled_close_date}
                      onChange={(e) =>
                        handleChange("scheduled_close_date", e.target.value)
                      }
                      disabled={!canEdit}
                      min={minDate}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white disabled:bg-gray-100"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        {form.scheduled_close_date
  ? `Site will be automatically closed on ${form.scheduled_close_date}.`
  : "Optionally schedule a future date to close this site (YYYY-MM-DD)."}
                    </p>
                  </div>
                )}

                <div className="pt-2">
                  <h3 className="text-sm font-semibold text-gray-800 mb-2">Local Contacts (up to 3)</h3>
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="mb-4 p-3 border border-gray-200 rounded bg-gray-50/50">
                      <p className="text-xs font-medium text-gray-500 mb-2">Contact {i + 1}</p>
                      <div className="grid gap-2">
                        <input
                          type="text"
                          placeholder="Name"
                          value={form.contacts[i]?.name || ""}
                          onChange={(e) => handleContactChange(i, "name", e.target.value)}
                          disabled={!canEdit}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100"
                        />
                        <input
                          type="text"
                          placeholder="Title"
                          value={form.contacts[i]?.title || ""}
                          onChange={(e) => handleContactChange(i, "title", e.target.value)}
                          disabled={!canEdit}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100"
                        />
                        <input
                          type="email"
                          placeholder="Email"
                          value={form.contacts[i]?.email || ""}
                          onChange={(e) => handleContactChange(i, "email", e.target.value)}
                          disabled={!canEdit}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100"
                        />
                        <input
                          type="text"
                          placeholder="Phone"
                          value={form.contacts[i]?.phone || ""}
                          onChange={(e) => handleContactChange(i, "phone", e.target.value)}
                          disabled={!canEdit}
                          className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {canEdit && (
                  <div className="pt-4 flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="inline-flex items-center justify-center rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-4 py-2 text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex items-center justify-center rounded-md bg-[var(--accent,#a51c1c)] px-4 py-2 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50"
                    >
                      {saving ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteDetailsModal;
